import { Component, EventEmitter, Host, Event, Prop, State, h, Method } from '@stencil/core';
import * as ListManagerInterfaces from '../add-to-myti-list/add-to-myti-list.interface';
import { AutocompleteChangeEventDetail, AutocompleteOption, HTMLTiAutocompleteElement, HTMLTiFormElementElement } from '@ticom/ui-components/dist';
import { fetchWithTimeout } from '@ticom/web-component-utils';
import { default as languageStrings } from '../../global/l10n/AutocompleteCpnList.json';

@Component({
    tag: 'ti-autocomplete-for-cpn-list',
    styleUrl: 'autocomplete-for-cpn-list.scss',
    shadow: true,
})
export class AutocompleteForCpnList {

    /**
     * Reference to ti-autocomplete element
     */
    private _autocompleteElement: HTMLTiAutocompleteElement;

    /**
     * App settings value to have settings based on user
     */
    private _appSettings: ListManagerInterfaces.AppSettings = null;

    /**
     * Reference to company select dropdown
     */
    private _companyDropdownRef: HTMLTiFormElementElement;

    /**
     * Used to store the company ids and names in a key value map
     */
    private _companyMap: Map<string, string> = null;

    /**
     * The cpn intervalId to keep track of the debounce intervals
     */
    private _cpnDebounceIntervalId: any = null;

    /**
     * Reference to cpns input element
     */
    private _cpnsInputElementRef: HTMLTiFormElementElement;

    /**
     * Reference to cpns input textarea
     */
    private _cpnsInputTextarea: HTMLTextAreaElement;

    /**
     * Value of cpns input in textarea
     */
    private _cpnsInputValue: string = null;

    /**
     * The intervalId to keep track of the debounce intervals
     */
    private _debounceIntervalId: any = null;

    /**
    * Reference to maximum number of cpns that can be added at once
    */
    private _editCpnsLimit: number = null;

    /**
     * Reference to Error message
     */
    private _errorElement: HTMLSpanElement;

    /**
     * Locale-specific set of language strings
     */
    private _localeStrings: Record<string, string>;

    /**
     * The previous value used for cpnsInputValue so that it doesn't repeat back to back
     */
    private _prevCpnInputValue: string = null;

    /**
     * The previous value used for fetch so that it doesn't repeat back to back
     */
    private _prevFetchValue: string = null;

    /**
     * Selected company ID in the company dropdown
     */
    private _selectedCompanyId: string = null;

    /**
     * Selected TI part number from the autocomplete
     */
    private _selectedTiPartNumber: string = null;

    /**
     * Constantly updating suggestion list based on what you type to show partial results
     */
    private _suggestionsList: AutocompleteOption[] = [];

    /**
     * Reference to ti-parts input autocomplete element
     */
    private _tiPartsInputElementRef: HTMLTiFormElementElement;

    /**
     * Reference to input element inside ti-autocomplete
     */
    private _tiPartsInputField: HTMLInputElement;

    /**
     * Store user info data (if present) from prop during load, and
     * utilize it always so the we can have prop as non-mutable
     */
    private _userInfo: ListManagerInterfaces.UserInfo = null;

    /**
     * Specifies progress state while data is loaded
     */
    @State() progressState: 'loading' | 'error' | 'ready' = 'loading';

    /**
     * Character limit for searching parts
     */
    @Prop() charLimit: number = ListManagerInterfaces.CHAR_LIMIT_FOR_SEARCH;

    /**
     * Specifies the context id for the autocomplete component so that add to list component can distinguish events
     */
    @Prop() contextId: string = null;

    /**
     * Specifies the delay of the debounce for parts info api call (in ms)
     */
    @Prop() debounceDurationMs: number = 500;

    /**
    * Base url for api calls
    */
    @Prop() domain: string;

    /**
    * Maximum number of cpns that can be added at once
    */
    @Prop() editCpnsLimit: number = null;

    /**
     * Specifies the locale
     */
    @Prop() locale: string = 'en-US';

    /**
     * Get the userInfo details if already available
     */
    @Prop() userInfo: ListManagerInterfaces.UserInfo = null;

    /**
     * Event to be emitted in case of any error
     */
    @Event() myTiAutosuggestErrorEvent: EventEmitter<ListManagerInterfaces.MyTiListManagerErrorEventDetail>;

    /**
     * Event to broadcast validity of selection
     */
    @Event() myTiBulkAddtoListEvent: EventEmitter<ListManagerInterfaces.MyTiBulkAddtoListEventDetail>;

    /**
     * component life cycle hooks
     */
    async componentWillLoad() {
        // load language strings for locale - fallback to 'en-US' or an empty object
        this._localeStrings = languageStrings[this.locale] || languageStrings['en-US'] || {};
        await this._initData();
    }

    /**
     * Method to get final selected data
     */
    @Method()
    async getItemsToAdd(): Promise<ListManagerInterfaces.CpnAssoc[]> {
        const validatedCpnAssocs: ListManagerInterfaces.CpnAssoc[] = this._cpnsInputValue.split(',').map((customerPartNumber: string) => {
            return {
                cpId: Number(this._selectedCompanyId),
                customerPartNumber: customerPartNumber.trim(),
                associatedPartNumber: this._selectedTiPartNumber
            };
        });
        return validatedCpnAssocs;
    }

    /**
     * Clear selected TI part number and disable add to list button
     */
    private _clearSelectedTiPartNumber() {
        // Set selected TI part number to null, display TI part number required error and fire event to disable add to list button
        this._selectedTiPartNumber = null;
        this._tiPartsInputElementRef.error = true;
        const tiPartInputErrorMsg: string = 'Error: No TI part is selected';
        this.myTiAutosuggestErrorEvent.emit({ contextId: this.contextId, errorCode: null, errorMsg: tiPartInputErrorMsg });
    }

    /**
     * Make api call to get the app settings
     */
    private async _getAppSettings(): Promise<void> {
        const apiUrl: string = this.domain
            ? this.domain + '/listmodel/api/v1/ext/secured/settings'
            : 'data/add-to-myti-list/app-settings.json';

        let errorResponseStatus: number = null;

        try {
            await this._makeApiCall(apiUrl, 'GET', null).then((apiResponse: Response) => {
                if (!apiResponse.ok) {
                    errorResponseStatus = apiResponse.status;
                    throw new Error('Error in getting App Settings');
                }
                return apiResponse.json();
            }).then((data: ListManagerInterfaces.AppSettings) => {
                this._appSettings = data;
                if (!this._editCpnsLimit) {
                    this._editCpnsLimit = this._appSettings ? this._appSettings.cpnListMaxItemsEditLimit : ListManagerInterfaces.CPN_MAX_EDIT_LIMIT;
                }
            });
        } catch (err) {
            this.myTiAutosuggestErrorEvent.emit({ contextId: this.contextId, errorCode: errorResponseStatus, errorMsg: err });
        }

    }

    /**
    * Return error content
    */
    private _getErrorContent() {
        return <span>{this._localeStrings['autocompleteErrorText']}</span>;
    }

    /**
     * Make api call for matching parts
     */
    private async _getPartInfo(value: string) {
        const apiUrl: string = this.domain
            ? `${this.domain}/listmodel/api/v1/ext/authpass/autocomplete/opn?q=${value}&order-type=${this._userInfo.isBacklog ? 'backlog' : 'store'}`
            : `data/autocomplete-mylist/parts.json`;

        let errorResponseStatus: number = null;
        try {
            // Perform a single API call
            const apiResponse = await this._makeApiCall(apiUrl, 'GET', null);

            if (!apiResponse.ok) {
                errorResponseStatus = apiResponse.status;
                throw new Error('Error in getting part suggestions');
            }

            const data = await apiResponse.json();
            const allData: { content: string; value: string; type: string }[] = [];

            if (data) {
                // Map the response to include type differentiation
                data.forEach((item: { partNumber: string; type: string }) => {
                    allData.push({
                        content: item.partNumber,
                        value: item.partNumber,
                        type: item.type
                    });
                });
            }

            // Set suggestions list
            this._suggestionsList = allData;
            this._autocompleteElement.setOptionsList(this._suggestionsList);
        } catch (err) {
            // don't set to error as a bad request due to unallowed characters shouldn't disable the whole component
            this.myTiAutosuggestErrorEvent.emit({ contextId: this.contextId, errorCode: errorResponseStatus, errorMsg: err });
        }
    }

    /**
     * Return the loading content
     */
    private _getLoadingContent() {
        return <div class="ti-autocomplete-cpn-progress-container">
            <ti-progress></ti-progress>
        </div>;
    }

    /**
     * Make api call to get user info
     */
    private async _getUserInfo() {
        const apiUrl: string = this.domain
            ? this.domain + '/listmodel/api/v1/ext/secured/user'
            : 'data/add-to-myti-list/user-info.json';

        let errorResponseStatus: number = null;
        try {
            await this._makeApiCall(apiUrl, 'GET', null).then((apiResponse: Response) => {
                if (!apiResponse.ok) {
                    errorResponseStatus = apiResponse.status;
                    throw new Error('Error in getting User Info');
                }
                return apiResponse.json();
            }).then((data: ListManagerInterfaces.UserInfo) => {
                this._userInfo = data;
            });
        } catch (err) {
            this.myTiAutosuggestErrorEvent.emit({ contextId: this.contextId, errorCode: errorResponseStatus, errorMsg: err });
        }
    }

    /**
     * Method to initialize data for props
     */
    private async _initData() {
        // initialize the props
        if (!this.userInfo) {
            await this._getUserInfo();
        } else {
            this._userInfo = this.userInfo;
        }

        if (this._userInfo?.currentCpId) {
            if (!this.editCpnsLimit) {
                await this._getAppSettings();
            } else {
                this._editCpnsLimit = this.editCpnsLimit;
            }
        } else {
            // If the user does not have a currentCpId then display error in the add association component
            this.progressState = 'error';
            return;
        }

        // If userInfo and editCpnsLimit are fetched and stored then display the core add association component else show error
        if (this._editCpnsLimit && this._userInfo) {
            this._selectedCompanyId = this._userInfo.currentCpId.toString();
            // Store company names corresponding to their company ids
            // Also add the company account account group corresponding to parent CpId
            if (this._userInfo.role === ListManagerInterfaces.SUPER_ADMIN_ROLE) {
                this._companyMap = new Map([
                    [this._userInfo.parentCpId.toString(), this._localeStrings['companyAccountGroupText']],
                    ...new Map(Object.entries(this._userInfo.childCompanies))
                ]);
            }
            this.progressState = 'ready';
        } else {
            this.progressState = 'error';
        }
    }

    /**
     * Generic method to make all api calls
     */
    private async _makeApiCall(apiUrl: string,
        methodType: ListManagerInterfaces.HttpMethodType,
        requestBody: any,
        timeout: number = ListManagerInterfaces.LIST_API_TIMEOUT_VALUE
    ): Promise<Response> {
        const apiResponse: Response = await fetchWithTimeout(apiUrl,
            {
                method: methodType,
                body: requestBody !== null && requestBody !== undefined ? JSON.stringify(requestBody) : null,
                credentials: 'include',
                headers: {
                    'Cache-Control': 'no-store, must-revalidate',
                    'Expires': '0',
                    'Content-Type': 'application/json'
                }
            }, timeout);

        return apiResponse;
    }

    /**
     * Method to handle company change from the dropdown
     */
    private _onCompanyChange(event: Event) {
        this._companyDropdownRef.error = false;
        this._selectedCompanyId = event.target['value'];
        this.myTiBulkAddtoListEvent.emit({ contextId: this.contextId, isValid: this._validateCpnData(), action: 'opn-search-for-cpn' });
    }

    /**
     * Method to handle cpn textarea input change
     */
    private _onCpnInputChange(ev: Event) {
        const input = ev.target as HTMLInputElement;
        // start interval for debounce
        const intervalId: NodeJS.Timeout = setInterval(() => {
            clearInterval(intervalId);
            if (intervalId === this._cpnDebounceIntervalId && this._prevCpnInputValue !== input.value) {
                // Wait for debounce intervals and perform validations to update input text and enable/disable add to list button
                this._cpnsInputElementRef.error = !input.value;
                this._prevCpnInputValue = input.value;
                if (input?.value) {
                    // Remove line breaks globally with comma
                    const cpnData = input.value.toString().trim().replace(/(\r\n|\n|\r)/g, ',') || '';
                    this._cpnsInputValue = cpnData.replace(/,+/g, ',');
                    this._cpnsInputTextarea.value = this._cpnsInputValue;
                    this.myTiBulkAddtoListEvent.emit({ contextId: this.contextId, isValid: this._validateCpnData(), action: 'opn-search-for-cpn' });
                } else {
                    const cpnInputErrorMsg: string = 'Error: Cpn input cannot be empty';
                    this.myTiAutosuggestErrorEvent.emit({ contextId: this.contextId, errorCode: null, errorMsg: cpnInputErrorMsg });
                }
            }
        }, this.debounceDurationMs);
        this._cpnDebounceIntervalId = intervalId;
    }

    /**
     * Method to modify the string pasted in the cpn text area
     */
    private _onPaste = (ev: ClipboardEvent) => {
        let data = '';
        // check for IE11 as clipboardData is not supported
        if (ev.clipboardData === undefined) {
            data = window['clipboardData'].getData('Text');
        } else {
            data = ev.clipboardData.getData('Text');
        }

        if (!data) {
            return;
        }
        data = data.trim().replace(/(\r\n|\n|\r)/g, ',') || '';    // replace line breaks globally with comma
        this._cpnsInputValue = data.replace(/,+/g, ',');
    };


    /**
     * Method to fetch parts on debounce during input
     */
    private _onSearchValueChange(value: string) {
        // Update error message content in the autocomplete slot and perform query length validations
        this._errorElement.textContent = value?.length < this.charLimit ? this._localeStrings['charLimitErrorText'] : this._localeStrings['noResultsText'];
        if (value?.length < this.charLimit) {
            this._errorElement.textContent = this._localeStrings['charLimitErrorText'];

            // if min length constraint violated, don't fetch results and empty suggestions
            this._prevFetchValue = value;
            this._suggestionsList = [];
            this._clearSelectedTiPartNumber();
            clearInterval(this._debounceIntervalId);
            return;
        }

        // start interval for debounce
        const intervalId: NodeJS.Timeout = setInterval(() => {
            clearInterval(intervalId);
            if (intervalId === this._debounceIntervalId && this._prevFetchValue !== value) {
                this._debounceIntervalId = null;
                this._clearSelectedTiPartNumber();
                // Fetch suggestions based on query value
                value = value.trim();
                if (value) {
                    this._prevFetchValue = value;
                    // make API call and populate results
                    this._getPartInfo(value);
                }
            }
        }, this.debounceDurationMs);
        this._debounceIntervalId = intervalId;
    }

    /**
     * Method to handle selections made on autocomplete element
     */
    private _onTiAutocompleteChange(event: CustomEvent<AutocompleteChangeEventDetail>) {
        const trigger: string = event.detail.trigger.toString();
        const value: string = event.detail.value;
        if (value && (trigger === 'listClick' || trigger === 'listEnter')) {
            // Set the selected TI part number, disable the tiPartsInput form error and emit the add to list event with validations performed for other fields
            this._tiPartsInputField.value = value;
            this._selectedTiPartNumber = value;
            this._tiPartsInputElementRef.error = false;
            this.myTiBulkAddtoListEvent.emit({ contextId: this.contextId, isValid: this._validateCpnData(), action: 'opn-search-for-cpn' });
        }
    }

    /**
     * Return true if all the required fields(selected company, TI part number, cpn input value) are available
     */
    private _validateCpnData() {
        // Javascript returns a string by default in the case of logical AND/OR operations
        // Hence, adding NOT operators before the expression to get boolean results
        return !!(this._selectedCompanyId && this._selectedTiPartNumber && this._cpnsInputValue);
    }

    render() {
        if (this.progressState === 'loading') {
            return <Host>{this._getLoadingContent()}</Host>;
        } else if (this.progressState === 'error') {
            return <Host>{this._getErrorContent()}</Host>;
        }

        return (
            <Host>
                <div class='ti-autocomplete-cpn-container'>
                    {/* If the user has parent cpId then add the company select dropdown */}
                    {this._userInfo.role === ListManagerInterfaces.SUPER_ADMIN_ROLE &&
                    <ti-form-element
                        label-text={this._localeStrings['selectCompanyLabel']}
                        required
                        class="ti-autocomplete-cpn-company-dropdown"
                        ref={(el) => this._companyDropdownRef = el}>
                        {/* Build the dropdown options in such a way that the labels will be the company names but the value will be the company id */}
                        <ti-select
                            class="ti-autocomplete-cpn-select"
                            labels={Array.from(this._companyMap.values()).join(',')}
                            options={Array.from(this._companyMap.keys()).join(',')}
                            value={this._selectedCompanyId}
                            onTiSelectChange={(e) => this._onCompanyChange(e)}>
                        </ti-select>
                        <div slot="error" class="ti-autocomplete-cpn-field-required-error">
                            <ti-svg-icon appearance="error" icon-set="objects" size="s">info-circle</ti-svg-icon>
                            <span>
                                {this._localeStrings['fieldRequiredErrorText'].replace('{0}', this._localeStrings['selectCompanyLabel'])}
                            </span>
                        </div>
                    </ti-form-element>
                    }
                    <ti-form-element
                        error={true}
                        label-text={this._localeStrings['searchTIPartLabel']}
                        required
                        class="ti-autocomplete-cpn-search-ti-part"
                        ref={(el) => this._tiPartsInputElementRef = el}>
                        <ti-autocomplete
                            ref={el => this._autocompleteElement = el}
                            onTiAutocompleteChange={ev => this._onTiAutocompleteChange(ev)}>
                            <input
                                ref={el => this._tiPartsInputField = el}
                                slot="input"
                                type="input"
                                placeholder={this._localeStrings['searchPlaceholderText']}
                                onInput={ev => this._onSearchValueChange((ev.target as HTMLInputElement).value)}
                            />
                            <span ref={el => this._errorElement = el} slot='no-results'>
                                {this._localeStrings['noResultsText']}
                            </span>
                        </ti-autocomplete>
                        <div slot="error" class="ti-autocomplete-cpn-field-required-error">
                            <ti-svg-icon appearance="error" icon-set="objects" size="s">info-circle</ti-svg-icon>
                            <span>
                                {this._localeStrings['fieldRequiredErrorText'].replace('{0}', this._localeStrings['searchTIPartLabel'])}
                            </span>
                        </div>
                    </ti-form-element>
                    <ti-form-element
                        error={true}
                        label-text={this._localeStrings['enterCPNsLabel']}
                        required
                        class="ti-autocomplete-cpn-textarea"
                        ref={(el) => this._cpnsInputElementRef = el}>
                        <div class='ti-autocomplete-cpn-enter-cpns-container'>
                            <span class='ti-autocomplete-cpn-enter-cpns-text'>{this._localeStrings['enterCPNstext']}</span>
                            <textarea
                                class="ti-autocomplete-cpn-enter-cpns-textarea"
                                value={this._cpnsInputValue}
                                onInput={(ev) => this._onCpnInputChange(ev)}
                                onPaste={this._onPaste}
                                placeholder={this._localeStrings['cpnTextareaPlaceholder'].replace('{0}', this._editCpnsLimit.toString())}
                                ref={(el) => this._cpnsInputTextarea = el}
                                rows={4}>
                            </textarea>
                        </div>
                        <div slot="error" class="ti-autocomplete-cpn-field-required-error">
                            <ti-svg-icon appearance="error" icon-set="objects" size="s">info-circle</ti-svg-icon>
                            <span>
                                {this._localeStrings['fieldRequiredErrorText'].replace('{0}', this._localeStrings['enterCPNsLabel'])}
                            </span>
                        </div>
                    </ti-form-element>
                </div>
            </Host>
        );
    }
}
